import React, { useEffect, useRef, useState } from "react";
import { IconButton, TextField, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../../../../UniversalComponents/DefaultButton";
import { fetchManagePermissionGroup } from "../../../../../utils/api/managePermissionApi";
import SelectComponent from "../../../../UniversalComponents/SelectComponent";
import { useLazyFetchPersonAllInOrgQuery } from "../../../../../utils/customer/customerTable";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../../../../utils/alertSlice";
import { getCompanyId, getUserToken } from "../../../../../utils/utilityFunctions/localServices";
import useRequireAuth from "../../../../../utils/useRequireAuth";
import { useCreateOrUpdateUserMutation } from "../../../../../utils/customer/customerTaskTable";
import ToolTipComponent from "../../../../UniversalComponents/ToolTipComponent";

const CreateUserPanel = ({ open, onClose, userData = {}, setRefetch }) => {
  const [groupData, setGroupData] = useState([]);
  const [errors, setErrors] = useState({});

  const drawerRef = useRef(null);

  useEffect(() => {
    drawerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);
  const initialFormdata = {
    email: "",
    full_name: "",
    reporting_manager_id: "",
    group_ids: [],
  };
  const [form, setForm] = useState(initialFormdata);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(form.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!form.full_name?.trim()) newErrors.full_name = "Name is required";
    if (!form.group_ids?.length) newErrors.group_ids = "At least one group must be selected";
    if (!form.reporting_manager_id) newErrors.reporting_manager_id = "Reporting manager is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const dispatch = useDispatch();

  const getGroupPermissionData = async () => {
    try {
      const response = await fetchManagePermissionGroup({ query: { key: "all_groups" } });
      if (response?.success) {
        setGroupData(response?.data);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    }
  };

  const token = useRequireAuth();
  console.log(token, "hellotoken");

  const [fetchPersonInTable, { data: personTableData }] = useLazyFetchPersonAllInOrgQuery({ token });

  useEffect(() => {
    fetchPersonInTable({});
  }, []);

  useEffect(() => {
    getGroupPermissionData();
  }, []);

  useEffect(() => {
    if (userData?.user_id) {
      setForm({
        email: userData?.email || "",
        full_name: userData?.name || "",
        reporting_manager_id: userData?.reporting_manager?.user_id || "",
        group_ids: userData?.assigned_groups?.map((group) => group.group_id) || [],
      });
    }
  }, [userData]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: field === "reporting_manager_id" ? value?.[0] : value,
    }));

    // If there's any value, clear the error for this field
    if (value && (typeof value === "string" ? value.trim() !== "" : true)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "",
      }));
    }
  };

  console.log(form, "formdataPrint");

  const [addUpdateUserList, { isLoading: AddUpdateLoading, isError, isSuccess, error }] =
    useCreateOrUpdateUserMutation();

  const handleSubmit = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!validateForm()) return;
    try {
      const response = await addUpdateUserList({ token, data: form }).unwrap();
      if (response?.success) {
        setForm(initialFormdata);
        dispatch(pushAlert({ type: "success", message: response?.message, visible: true }));
        onClose();
        setRefetch((prev) => !prev);
      } else {
        dispatch(pushAlert({ type: "danger", message: response?.message, visible: true }));
      }
    } catch (err) {
      dispatch(pushAlert({ type: "danger", message: err?.error || err?.message, visible: true }));
    }
  };

  return (
    <div className="flex flex-col border-[1px] border-gray-200 h-full" onClose={onClose}>
      <div className="p-4 flex flex-col h-fit justify-between">
        {/* Header */}
        <div ref={drawerRef}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[16px] font-medium text-[#1E293B]">{userData?.user_id ? "Edit" : "Create"} User</h2>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Email */}
          <div className="mb-5">
            <label className="text-sm text-gray-600 block mb-1">
              Email <span className="text-gray-400 text-xs">(You can add multiple users to a single group)</span>
            </label>

            {userData?.user_id ? (
              <ToolTipComponent
                text={
                  <div className="text-[#d32f2f] font-poppins text-sm not-italic font-normal leading-none">
                    Email Address can't be updated
                  </div>
                }
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter Email"
                  value={form.email}
                  onChange={handleChange("email")}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  InputProps={{
                    readOnly: true,
                  }}
                  inputProps={{
                    className: "bg-[#f9fafb] text-gray-500 cursor-not-allowed opacity-80",
                  }}
                />
              </ToolTipComponent>
            ) : (
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Email"
                value={form.email}
                onChange={handleChange("email")}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            )}
          </div>

          {/* Name */}
          <div className="mb-5">
            <label className="text-sm text-gray-600 block mb-1">Full Name</label>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Name"
              value={form.full_name}
              onChange={handleChange("full_name")}
              error={errors.full_name}
              helperText={errors.full_name}
            />
          </div>

          {/* Group */}
          <div className="mb-5">
            <label className="text-sm text-gray-600 block mb-1">
              Select Group <span className="text-gray-400 text-xs">(Assign a group to the user)</span>
            </label>
            <SelectComponent
              name="group_ids"
              options={groupData}
              labelKey="group_name"
              valueKey="group_id"
              multiSelect
              onChange={handleChange("group_ids")}
              value={groupData?.filter((item) => form.group_ids.includes(item.group_id))}
            />
            {errors.group_ids && (
              <p className="text-xs text-[#d32f2f] mt-1 pl-4 font-roboto font-500 leading-[17px]">{errors.group_ids}</p>
            )}
          </div>

          {/* Reporting Manager */}
          <div className="mb-5">
            <label className="text-sm text-gray-600 block mb-1">Reporting Manager</label>
            <SelectComponent
              name="reporting_manager_id"
              options={personTableData?.data}
              labelKey="name"
              valueKey="id"
              subLabelKey="email"
              personList={true}
              // onChange={() => {
              //   handleChange("reporting_manager_id");
              // }}
              onChange={handleChange("reporting_manager_id")}
              showSearch={true}
              value={personTableData?.data.filter((item) => item.id == form.reporting_manager_id)}
            />
            {errors.reporting_manager_id && (
              <p className=" text-[#d32f2f] mt-1 pl-4 text-xs font-roboto font-500 leading-[17px]">
                {errors.reporting_manager_id}
              </p>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="bg-primary-90 hover:bg-primary" onClick={handleSubmit}>
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateUserPanel;
